import NAV_LIST from 'app/configs/navList.config';
import { INavList } from 'app/interfaces/nav-list/navList.interface';
import ROUTER_PATH from 'app/routes/routerPath';
import LocalStorageService from 'app/services/localStorage';
import ImagePath from 'app/utilities/imagePath';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import ChangePassword from 'app/components/ChangePassword/ChangePassword';
import './NavComponent.scss';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { getLenderSummaryHeaderAction } from 'app/redux/actions/PortofolioAction';
import { RootState } from 'app/redux/store';
import rdlModalStyles from 'config/nonRDLmodal.config';
import { getTkbAction } from 'app/redux/actions/NavHeaderAction';
import customModalStyles from '../../../config/modal.config';
import 'react-toastify/dist/ReactToastify.css';
import WithdrawModal from '../WithdrawModal/WithdrawModal';
import ProfileModal from '../ProfileModal/ProfileModal';
import RDLModal from '../RDLModal/RDLModal';

interface toastType {
    success: string,
    error: string,
    info: string,
    warning: string,
    default: string,
    dark: string
}

const contextClass: toastType = {
    success: "toastr success",
    error: "toastr error",
    info: "bg-gray-600",
    warning: "toastr warning",
    default: "bg-indigo-600",
    dark: "bg-white-600 font-gray-300",
};

type MetricItem = {
    label: string;
    value: string;
};

type MetricPhase = 'enter' | 'exit';

const METRIC_ROTATE_INTERVAL_MS = 8000;
const METRIC_ROTATE_FADE_OUT_MS = 280;

const toMetricItems = (
    data?: Array<Record<string, string>> | Record<string, string>
): MetricItem[] => {
    if (!data) {
        return [];
    }

    const entries = Array.isArray(data) ? data : [data];

    return entries.flatMap((entry) => Object.entries(entry || {}).map(([label, value]) => ({
        label,
        value: value || '—',
    })));
};

function useMetricRotator(items: MetricItem[]): {
    index: number;
    phase: MetricPhase;
    primary: MetricItem | undefined;
} {
    const [index, setIndex] = useState(0);
    const [phase, setPhase] = useState<MetricPhase>('enter');

    useEffect(() => {
        setIndex(0);
        setPhase('enter');
    }, [items.length]);

    useEffect(() => {
        if (items.length <= 1) {
            return undefined;
        }

        let fadeTimeoutId: number | undefined;
        const intervalId = window.setInterval(() => {
            setPhase('exit');
            fadeTimeoutId = window.setTimeout(() => {
                setIndex((prev) => (prev + 1) % items.length);
                setPhase('enter');
            }, METRIC_ROTATE_FADE_OUT_MS);
        }, METRIC_ROTATE_INTERVAL_MS);

        return () => {
            window.clearInterval(intervalId);
            if (fadeTimeoutId) {
                window.clearTimeout(fadeTimeoutId);
            }
        };
    }, [items.length]);

    return {
        index,
        phase,
        primary: items[index] || items[0],
    };
}

const NavComponent = function init(): React.ReactElement {
    const navigate = useNavigate();
    const user = LocalStorageService.getUserData();
    const isLoggedIn = LocalStorageService.isLoggedIn();
    const [navList] = useState(NAV_LIST);
    const [isExpand, setExpand] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openWithdrawModal, setWithdrawModal] = useState(false);
    const [openProfileModal, setProfileModal] = useState(false);
    const [openNoRDLModal, setNoRDLModal] = useState(false);
    const [openTkbDropdown, setOpenTkbDropdown] = useState(false);
    const [openTkpDropdown, setOpenTkpDropdown] = useState(false);
    const metricsRef = useRef<HTMLDivElement>(null);
    const withdrawState = useSelector((state: RootState) => state.withdrawReducer);
    const { successOnWithdraw, errorOnWithdraw } = withdrawState;
    const dispatch = useDispatch();
    const coorporateProfile = useSelector((state: RootState) => state.coorporateProfile);
    const navHeaderData = useSelector((state: RootState) => state.navHeaderReducer);
	const comproData = navHeaderData?.tkb?.data;
	const tkbData = comproData?.find((item) => item.tkb)?.tkb;
	const tkpData = comproData?.find((item) => item.tkp)?.tkp;

    const tkbItems = toMetricItems(tkbData);
    const tkpItems = toMetricItems(tkpData);
    const { index: tkbIndex, phase: tkbPhase, primary: tkbPrimary } = useMetricRotator(tkbItems);
    const { index: tkpIndex, phase: tkpPhase, primary: tkpPrimary } = useMetricRotator(tkpItems);

    useEffect(() => {
        if (successOnWithdraw || errorOnWithdraw) {
            setWithdrawModal(false);
        }
    }, [successOnWithdraw, errorOnWithdraw, navHeaderData]);

    useEffect(() => {
		dispatch(getTkbAction());
    }, []);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (metricsRef.current && !metricsRef.current.contains(event.target as Node)) {
                setOpenTkbDropdown(false);
                setOpenTkpDropdown(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function logoOnClick(): void {
        navigate(ROUTER_PATH.index);
    }

    function profileOnClick(): void {
        setOpenTkbDropdown(false);
        setOpenTkpDropdown(false);
        setExpand(!isExpand);
    }

    function showTkbDropdown(): void {
        setExpand(false);
        setOpenTkpDropdown(false);
        setOpenTkbDropdown(true);
    }

    function showTkpDropdown(): void {
        setExpand(false);
        setOpenTkbDropdown(false);
        setOpenTkpDropdown(true);
    }

    function toggleTkbDropdown(event: React.MouseEvent): void {
        event.stopPropagation();
        if (openTkbDropdown) {
            setOpenTkbDropdown(false);
            return;
        }
        showTkbDropdown();
    }

    function toggleTkpDropdown(event: React.MouseEvent): void {
        event.stopPropagation();
        if (openTkpDropdown) {
            setOpenTkpDropdown(false);
            return;
        }
        showTkpDropdown();
    }

    function withdrawOnClick(): void {
        dispatch(getLenderSummaryHeaderAction());
        setWithdrawModal(true);
    }

    function profileLabelOnClick(): void {
        setProfileModal(true);
    }

    function RDLChecking(): void {
        setNoRDLModal(true);
    }

    function navOnClick(nav: INavList): void {
        // TODO: need to enhance the function for more dynamically
        if (nav.label === "Keluar") {
            LocalStorageService.clearData();
            window.location.reload();
        } else if (nav.label === "Tarik Dana") {
            withdrawOnClick();
        } else if (nav.label === 'Deposit') {
            if (coorporateProfile.accountType) {
                navigate(ROUTER_PATH.deposit);
            } else {
                RDLChecking();
            }
        } else if (nav.label === 'Profil') {
            profileLabelOnClick();
        } else {
            setOpenModal(true);
        }
    }

    function renderNavList(nav: INavList): ReactElement {
        return (
            <button
                key={nav.label}
                type="button"
                className="nav-list"
                onClick={() => navOnClick(nav)}
            >
                <p>{nav.label}</p>
            </button>
        );
    }

    function renderMetricMenu(items: MetricItem[]): ReactElement {
        return (
            <div className="metric-dropdown-menu">
                {items.map((item) => (
                    <div key={item.label} className="metric-dropdown-item">
                        <span className="metric-dropdown-label">{item.label}</span>
                        <span className="metric-dropdown-value">{item.value}</span>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="nav-container">
            <ToastContainer
                position="top-center"
                toastClassName={(type) => `${contextClass[type?.type || 'default']}`}
                closeButton={false}
                hideProgressBar
                autoClose={6000}
                limit={1}
            />
            {isLoggedIn && (
                <div className="nav">
                    <div className="logo-container">
                        <button
                            type="button"
                            className="btn-img"
                            onClick={logoOnClick}
                        >
                            <img
                                className="nav-logo"
                                src={ImagePath.lenderLogo.src}
                                alt={ImagePath.lenderLogo.alt}
                            />
                        </button>
                        <hr />
                        <div className="app-title">
                            <h4>
                                Lender Dashboard
                            </h4>
                        </div>
                    </div>
                    <div className="nav-item d-flex">
                        <div className="nav-metrics" ref={metricsRef}>
                            <div
                                className={`metric-dropdown ${openTkbDropdown ? 'expand' : ''}`}
                                onMouseEnter={showTkbDropdown}
                                onMouseLeave={() => setOpenTkbDropdown(false)}
                            >
                                <button
                                    type="button"
                                    className="metric-trigger metric-trigger--tkb"
                                    onClick={toggleTkbDropdown}
                                    aria-expanded={openTkbDropdown}
                                >
                                    <span
                                        key={`tkb-${tkbPrimary?.label || 'empty'}-${tkbIndex}`}
                                        className={`metric-trigger-text metric-trigger-text--${tkbPhase}`}
                                    >
                                        {tkbPrimary?.label || 'TKB'}
:
{' '}
                                        <span className="metric-trigger-value">
                                            {tkbPrimary?.value || '—'}
                                        </span>
                                    </span>
                                </button>
                                {renderMetricMenu(tkbItems)}
                            </div>

                            <div className="metric-divider" />

                            <div
                                className={`metric-dropdown ${openTkpDropdown ? 'expand' : ''}`}
                                onMouseEnter={showTkpDropdown}
                                onMouseLeave={() => setOpenTkpDropdown(false)}
                            >
                                <button
                                    type="button"
                                    className="metric-trigger metric-trigger--tkp"
                                    onClick={toggleTkpDropdown}
                                    aria-expanded={openTkpDropdown}
                                >
                                    <span
                                        key={`tkp-${tkpPrimary?.label || 'empty'}-${tkpIndex}`}
                                        className={`metric-trigger-text metric-trigger-text--${tkpPhase}`}
                                    >
                                        {tkpPrimary?.label || 'TKP'}
:
{' '}
                                        {tkpPrimary?.value || '—'}
                                    </span>
                                </button>
                                {renderMetricMenu(tkpItems)}
                            </div>
                        </div>
                        <div
                            onKeyPress={profileOnClick}
                            onClick={profileOnClick}
                            tabIndex={0}
                            role="button"
                            className={`profile card ${isExpand ? "expand" : ""}`}
                        >
                            <p className="profile-name">
                                {user.userName}
                            </p>
                            <img
                                className="arrow"
                                src={ImagePath.blackArrowDown.src}
                                alt={ImagePath.blackArrowDown.alt}
                            />
                            <div className="nav-list-container">
                                {navList.map((nav) => renderNavList(nav))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                isOpen={openModal}
                ariaHideApp={false}
                style={customModalStyles}
            >
                <ChangePassword
                    setOpenModal={setOpenModal}
                />
            </Modal>
            <Modal
                isOpen={openWithdrawModal}
                ariaHideApp={false}
                style={customModalStyles}
            >
                <WithdrawModal
                    setOpenModal={setWithdrawModal}
                />
            </Modal>
            <Modal
                isOpen={openProfileModal}
                ariaHideApp={false}
                style={customModalStyles}
            >
                <ProfileModal
                    setOpenModal={setProfileModal}
                />
            </Modal>
            <Modal
                isOpen={openNoRDLModal}
                ariaHideApp={false}
                style={rdlModalStyles}
            >
                <RDLModal
                    setOpenModal={setNoRDLModal}
                />
            </Modal>
        </div>
    );
};

export default NavComponent;
